import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputBase,
  Menu,
  MenuItem,
  TextField,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import {
  getDatos,
  getDinatran,
  getTruckImagen,
  updateDatosVehiculo,
} from "../../services/vehiculos.services";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import secureStorage from "../../utils/secureStorage";

const COUNTRY_OPTIONS = [
  { label: "Paraguay", value: "PY" },
  { label: "Brasil", value: "BR" },
];

const vehicleValidationSchema = yup.object({
  brand: yup.string().required("Marca requerida"),
  model: yup.string().required("Modelo requerido"),
  chapa: yup.string().required("Chapa requerida"),
  country: yup
    .string()
    .oneOf(COUNTRY_OPTIONS.map((option) => option.value))
    .required("País requerido"),
  chasis: yup.string(),
  year: yup.number(),
  axes: yup.number(),
  nroHabilitacion: yup.number(),
  propietario: yup.string(),
  ruc: yup.string(),
  situacion: yup.string(),
  vencimiento: yup.string(),
  capacidad: yup.string().required("Capacidad requerida"),
  fotos: yup.string(),
});

const prepareInitialValues = (vehicle) => ({
  brand: vehicle?.brand ?? "",
  model: vehicle?.model ?? "",
  chapa: vehicle?.chapa ?? "",
  country: vehicle?.country ?? COUNTRY_OPTIONS[0].value,
  chasis: vehicle?.chasis ?? "",
  year: vehicle?.year ?? "",
  axes: vehicle?.axes ?? "",
  nroHabilitacion: vehicle?.nro_habilitacion ?? vehicle?.nroHabilitacion ?? "",
  propietario: vehicle?.propietario ?? "",
  ruc: vehicle?.ruc_propietario ?? vehicle?.ruc ?? "",
  situacion: vehicle?.situacion ?? "",
  vencimiento: vehicle?.vencimiento ?? "",
  capacidad:
    vehicle?.capacidad ??
    vehicle?.capacidad_carga ??
    vehicle?.capacidadCarga ??
    vehicle?.peso ??
    "",
  fotos: Array.isArray(vehicle?.fotos)
    ? vehicle.fotos.join("|")
    : vehicle?.fotos ?? "",
});

const normalizeString = (value) => (typeof value === "string" ? value.trim() : "");

const normalizePhotos = (value) => {
  if (!value) {
    return "";
  }

  return value
    .split("|")
    .map((url) => url.trim())
    .filter(Boolean)
    .join("|");
};

const extractPhotoUrls = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((url) => (typeof url === "string" ? url.trim() : ""))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("|")
      .map((url) => url.trim())
      .filter(Boolean);
  }

  if (value && typeof value === "object" && typeof value.url === "string") {
    return [value.url.trim()].filter(Boolean);
  }

  return [];
};

const getPrimaryPhotoFromVehicle = (vehicle) => {
  if (!vehicle) {
    return "";
  }

  const rawFotos =
    vehicle.fotos ??
    vehicle.foto ??
    vehicle.photo ??
    vehicle.photoUrl ??
    vehicle.foto_url ??
    "";

  const urls = extractPhotoUrls(rawFotos);
  return urls.length ? urls[0] : "";
};

const getPhotoUrlsFromVehicle = (vehicle) => {
  if (!vehicle) {
    return [];
  }

  const candidateFotos =
    vehicle.fotos ??
    vehicle.foto ??
    vehicle.photo ??
    vehicle.photoUrl ??
    vehicle.foto_url ??
    [];

  return extractPhotoUrls(candidateFotos);
};

const buildVehiclePayload = (values, idEmpresa) => {
  const payload = {
    brand: normalizeString(values.brand),
    model: normalizeString(values.model),
    chapa: normalizeString(values.chapa).toUpperCase(),
    id_empresa:
      typeof idEmpresa === "number" && !Number.isNaN(idEmpresa) ? idEmpresa : undefined,
    chasis: normalizeString(values.chasis),
    year: values.year,
    eje: values.axes,
    nro_habilitacion: values.nroHabilitacion || 0,
    propietario: normalizeString(values.propietario),
    ruc_propietario: normalizeString(values.ruc) || " ",
    situacion: normalizeString(values.situacion) || " ",
    vencimiento: normalizeString(values.vencimiento),
    capacidad: normalizeString(values.capacidad),
    fotos: normalizePhotos(values.fotos) || " ",
  };

  if (payload.id_empresa === undefined) {
    delete payload.id_empresa;
  }

  return payload;
};

const VehicleEditDialog = ({ open, onClose, vehicle, onSubmit, onPreviewPhotos }) => {
  const initialValues = useMemo(() => prepareInitialValues(vehicle), [vehicle]);
  const [isDinatranLoading, setIsDinatranLoading] = useState(false);
  const [isAutoLookupScheduled, setIsAutoLookupScheduled] = useState(false);
  const lastLookupRef = useRef({ chapa: "", country: "" });

  const handleConsultarDinatran = useCallback(
    async (lookupPayload, setFieldValue) => {
      const chapaInput = lookupPayload.chapa ? lookupPayload.chapa.trim() : "";
      const normalizedChapa = chapaInput.toUpperCase();
      const selectedCountry = lookupPayload.country || COUNTRY_OPTIONS[0].value;

      if (!normalizedChapa) {
        toast.error("Ingrese la chapa del vehículo antes de consultar.");
        return;
      }

      lastLookupRef.current = { chapa: normalizedChapa, country: selectedCountry };
      setIsAutoLookupScheduled(false);
      setIsDinatranLoading(true);

      try {
        const response = await getDinatran(normalizedChapa, selectedCountry);
        let vehicleData = response?.data ?? response?.vehicle ?? response;

        if (Array.isArray(vehicleData)) {
          vehicleData = vehicleData[0];
        }

        if (!vehicleData) {
          toast.error("No se encontraron datos en Dinatran para la chapa ingresada.");
          return;
        }

        const resolvedChapa = vehicleData?.plate || vehicleData?.chapa || normalizedChapa;
        const resolvedBrand = vehicleData?.brand || vehicleData?.Marca;
        const resolvedModel = vehicleData?.Tipo || vehicleData?.modelo;
        const chasisNumber =
          vehicleData?.chasis ||
          vehicleData?.Chasis ||
          vehicleData?.chassis ||
          vehicleData?.Chassis ||
          vehicleData?.vin ||
          vehicleData?.VIN ||
          "";
        const yearValue = vehicleData?.Anio || vehicleData?.anioFabricacion || "";
        const axesValue = vehicleData?.ejes || vehicleData?.Ejes || "";
        const nroHabilitacionValue = vehicleData?.NroHabilita || "";
        const propietarioValue = vehicleData?.propietario || vehicleData?.Propietario || "";
        const rucValue = vehicleData?.ruc || vehicleData?.Ruc || vehicleData?.RUC || "";
        const situacionValue =
          vehicleData?.situacion || vehicleData?.Situacion || vehicleData?.resultado || "";
        const vencimientoValue =
          vehicleData?.Vencimiento ||
          vehicleData?.FechaVencimiento ||
          vehicleData?.vencimiento ||
          "";
        const capacidadCargaValue =
          vehicleData?.capacidadCarga ||
          vehicleData?.CapacidadCarga ||
          vehicleData?.peso ||
          vehicleData?.Peso ||
          "";

        if (resolvedChapa) {
          const upperChapa = String(resolvedChapa).toUpperCase();
          setFieldValue("chapa", upperChapa);
          lastLookupRef.current = { chapa: upperChapa, country: selectedCountry };
        }

        if (resolvedBrand) {
          setFieldValue("brand", resolvedBrand);
        }

        if (resolvedModel) {
          setFieldValue("model", resolvedModel);
        }

        setFieldValue("chasis", chasisNumber || "");
        setFieldValue("year", yearValue || "");
        setFieldValue("axes", axesValue || "");
        setFieldValue("nroHabilitacion", nroHabilitacionValue || "");
        setFieldValue("propietario", propietarioValue || "");
        setFieldValue("ruc", rucValue || "");
        setFieldValue("situacion", situacionValue || "");
        setFieldValue("vencimiento", vencimientoValue || "");
        setFieldValue("capacidad", capacidadCargaValue || "");

        let fotosList = Array.isArray(vehicleData?.fotos) ? vehicleData.fotos : [];

        if (!fotosList.length) {
          const inspections = Array.isArray(vehicleData?.Itv)
            ? vehicleData.Itv
            : Array.isArray(vehicleData?.itv)
            ? vehicleData.itv
            : [];

          const latestInspection = inspections.length ? inspections[inspections.length - 1] : null;

          if (
            latestInspection?.CodigoTaller &&
            latestInspection?.AraItv &&
            latestInspection?.NumeroPlanilla
          ) {
            const inspectionResponse = await getTruckImagen(
              latestInspection.CodigoTaller,
              latestInspection.AraItv,
              latestInspection.NumeroPlanilla
            );

            const inspectionPayload = inspectionResponse?.data ?? inspectionResponse;
            const inspectionFotos = Array.isArray(inspectionPayload?.fotos)
              ? inspectionPayload.fotos
              : Array.isArray(inspectionPayload?.data?.fotos)
              ? inspectionPayload.data.fotos
              : [];

            fotosList = inspectionFotos;
          }
        }

        const fotosValue = Array.isArray(fotosList)
          ? fotosList.filter(Boolean).join("|")
          : typeof fotosList === "string"
          ? fotosList
          : "";

        setFieldValue("fotos", fotosValue);
      } catch (error) {
        const message = error?.message || "Error al consultar Dinatran.";
        toast.error(message);
      } finally {
        setIsDinatranLoading(false);
      }
    },
    []
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Editar vehículo</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={vehicleValidationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => {
          const photoUrls = extractPhotoUrls(values.fotos);

          const handlePreview = () => {
            if (onPreviewPhotos && photoUrls.length) {
              onPreviewPhotos(photoUrls);
            }
          };

          return (
            <form onSubmit={handleSubmit}>
              <DinatranAutoLookupWatcher
                values={values}
                lastLookupRef={lastLookupRef}
                onSchedule={() => setIsAutoLookupScheduled(true)}
                onCancel={() => setIsAutoLookupScheduled(false)}
                onTrigger={(payload) => handleConsultarDinatran(payload, setFieldValue)}
              />
              <DialogContent dividers>
                
                <Box
                  display="grid"
                  gap="16px"
                  gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                >
                <TextField
                  label="País de origen"
                  name="country"
                  select
                  variant="filled"
                  value={values.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.country && errors.country)}
                  helperText={touched.country && errors.country}
                >
                  {COUNTRY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Marca"
                  name="brand"
                  variant="filled"
                  value={values.brand}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.brand && errors.brand)}
                  helperText={touched.brand && errors.brand}
                />
                <TextField
                  label="Modelo"
                  name="model"
                  variant="filled"
                  value={values.model}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.model && errors.model)}
                  helperText={touched.model && errors.model}
                />
                <TextField
                  label="Chapa"
                  name="chapa"
                  variant="filled"
                  value={values.chapa}
                  onChange={(event) => {
                    const upperValue = event.target.value.toUpperCase();
                    handleChange({
                      target: {
                        name: "chapa",
                        value: upperValue,
                      },
                    });
                  }}
                  onBlur={handleBlur}
                  error={Boolean(touched.chapa && errors.chapa)}
                  helperText={touched.chapa && errors.chapa}
                  InputProps={{
                    endAdornment:
                      isAutoLookupScheduled || isDinatranLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} sx={{ color: "#cedc00" }} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
                <TextField
                  label="Chasis"
                  name="chasis"
                  variant="filled"
                  value={values.chasis}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.chasis && errors.chasis)}
                  helperText={touched.chasis && errors.chasis}
                />
                <TextField
                  label="Año"
                  name="year"
                  variant="filled"
                  value={values.year}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.year && errors.year)}
                  helperText={touched.year && errors.year}
                />
                <TextField
                  label="Ejes"
                  name="axes"
                  variant="filled"
                  value={values.axes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.axes && errors.axes)}
                  helperText={touched.axes && errors.axes}
                />
                <TextField
                  label="Nro. habilitación"
                  name="nroHabilitacion"
                  variant="filled"
                  value={values.nroHabilitacion}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.nroHabilitacion && errors.nroHabilitacion)}
                  helperText={touched.nroHabilitacion && errors.nroHabilitacion}
                />
                <TextField
                  label="Propietario"
                  name="propietario"
                  variant="filled"
                  value={values.propietario}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.propietario && errors.propietario)}
                  helperText={touched.propietario && errors.propietario}
                />
                <TextField
                  label="RUC"
                  name="ruc"
                  variant="filled"
                  value={values.ruc}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.ruc && errors.ruc)}
                  helperText={touched.ruc && errors.ruc}
                />
                <TextField
                  label="Situación"
                  name="situacion"
                  variant="filled"
                  value={values.situacion}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.situacion && errors.situacion)}
                  helperText={touched.situacion && errors.situacion}
                />
                <TextField
                  label="Vencimiento"
                  name="vencimiento"
                  variant="filled"
                  value={values.vencimiento}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.vencimiento && errors.vencimiento)}
                  helperText={touched.vencimiento && errors.vencimiento}
                />
                <TextField
                  label="Capacidad de carga"
                  name="capacidad"
                  variant="filled"
                  value={values.capacidad}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.capacidad && errors.capacidad)}
                  helperText={touched.capacidad && errors.capacidad}
                />
                  <TextField
                    label="Fotos"
                    name="fotos"
                    variant="filled"
                    value={values.fotos}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.fotos && errors.fotos)}
                    helperText={touched.fotos && errors.fotos}
                    sx={{ display: "none" }}
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="wrap"
                  gap="12px"
                  mb={photoUrls.length ? 2 : 0}
                >
                  {photoUrls.length ? (
                    photoUrls.map((url, index) => (
                      <Box
                        key={`${url}-${index}`}
                        component="img"
                        src={url}
                        alt={`Foto ${values.chapa || "vehículo"}`}
                        onClick={handlePreview}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handlePreview();
                          }
                        }}
                        role={onPreviewPhotos && photoUrls.length ? "button" : undefined}
                        tabIndex={onPreviewPhotos && photoUrls.length ? 0 : -1}
                        sx={{
                          width: "100%",
                          maxWidth: 220,
                          height: 140,
                          objectFit: "cover",
                          borderRadius: "6px",
                          boxShadow: 3,
                          cursor: onPreviewPhotos && photoUrls.length ? "pointer" : "default",
                        }}
                      />
                    ))
                  ) : (
                    <Alert severity="info" sx={{ width: "100%" }}>
                      Sin imagen disponible
                    </Alert>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={18} /> : "Guardar"}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

const PhotoPreviewDialog = ({ open, photos, onClose }) => {
  const safePhotos = Array.isArray(photos) ? photos : [];
  // console.log("🚀 ~ PhotoPreviewDialog ~ safePhotos:", safePhotos)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Fotos del vehículo</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexWrap="wrap" gap="16px" justifyContent="center">
          {safePhotos.map((url, index) => (
            <Box
              key={`${url}-${index}`}
              component="img"
              src={url}
              alt={`Foto ${index + 1}`}
              sx={{
                width: "100%",
                maxWidth: 320,
                height: 200,
                objectFit: "cover",
                borderRadius: "8px",
                boxShadow: 4,
              }}
            />
          ))}
          {!safePhotos.length && (
            <Alert severity="info" sx={{ width: "100%" }}>
              Sin imagen disponible
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

const TruckQuery = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [vehicles, setVehicles] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPhotoPreviewOpen, setIsPhotoPreviewOpen] = useState(false);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);

  const codEmpresa = useMemo(() => {
    const storedValue = secureStorage.getItem("cod_empresa");
    const parsed = storedValue ? parseInt(storedValue, 10) : undefined;
    return Number.isNaN(parsed) ? undefined : parsed;
  }, []);

  const loadVehicles = useCallback(async () => {
    setIsFetching(true);
    setErrorMessage("");

    try {
      const response = await getDatos();
      const items = Array.isArray(response) ? response : [];
      setVehicles(items);
    } catch (error) {
      const message = error?.message || "No se pudo obtener el listado de vehículos.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const filteredVehicles = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      return vehicles;
    }

    return vehicles.filter((vehicle) => {
      const searchableValues = [
        vehicle?.chapa,
        vehicle?.brand,
        vehicle?.model,
        vehicle?.propietario,
        vehicle?.ruc_propietario,
        vehicle?.ruc,
        vehicle?.situacion,
        vehicle?.nro_habilitacion,
      ];

      return searchableValues.some(
        (value) => typeof value === "string" && value.toLowerCase().includes(term)
      );
    });
  }, [vehicles, searchValue]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleMenuClick = (event, vehicle) => {
    setAnchorEl(event.currentTarget);
    setSelectedVehicle(vehicle);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setIsEditDialogOpen(false);
    setSelectedVehicle(null);
  };

  const openPhotoPreview = useCallback((urls) => {
    if (!Array.isArray(urls) || !urls.length) {
      return;
    }

    setPhotoPreviewUrls(urls);
    setIsPhotoPreviewOpen(true);
  }, []);

  const closePhotoPreview = useCallback(() => {
    setIsPhotoPreviewOpen(false);
    setPhotoPreviewUrls([]);
  }, []);

  const handlePhotoCellClick = useCallback(
    (event, vehicle) => {
      event.preventDefault();
      event.stopPropagation();

      const urls = getPhotoUrlsFromVehicle(vehicle);
      openPhotoPreview(urls);
    },
    [openPhotoPreview]
  );

  const handleEditSubmit = async (values, formikHelpers) => {
    if (!selectedVehicle?.id) {
      toast.error("El registro seleccionado no es válido.");
      formikHelpers.setSubmitting(false);
      return;
    }
    

    try {
      const payload = buildVehiclePayload(
        values,
        selectedVehicle.id_empresa ?? codEmpresa
      );
      await updateDatosVehiculo(selectedVehicle.id, payload);
      toast.success("Vehículo actualizado correctamente.");
      await loadVehicles();
      setIsEditDialogOpen(false);
      setSelectedVehicle(null);
    } catch (error) {
      const message = error?.message || "No se pudo actualizar el vehículo.";
      toast.error(message);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      
      {
        field: "actions",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton onClick={(event) => handleMenuClick(event, params.row)}>
            <MenuIcon />
          </IconButton>
        ),
      },
      { field: "chapa", headerName: "Chapa", flex: 0.8, minWidth: 120 },
      { field: "brand", headerName: "Marca", flex: 1, minWidth: 140 },
      { field: "model", headerName: "Modelo", flex: 1, minWidth: 140 },
      {
        field: "propietario",
        headerName: "Propietario",
        flex: 1.2,
        minWidth: 180,
        valueGetter: (params) => params.row.propietario ?? "",
      },
      {
        field: "ruc_propietario",
        headerName: "RUC",
        flex: 0.9,
        minWidth: 130,
        valueGetter: (params) => params.row.ruc_propietario ?? params.row.ruc ?? "",
      },
      {
        field: "eje",
        headerName: "Ejes",
        flex: 0.5,
        minWidth: 100,
        valueGetter: (params) => params.row.eje ?? params.row.axes ?? "",
      },
      {
        field: "nro_habilitacion",
        headerName: "Nro. habilitación",
        flex: 0.9,
        minWidth: 160,
        valueGetter: (params) =>
          params.row.nro_habilitacion ?? params.row.nroHabilitacion ?? "",
      },
      {
        field: "situacion",
        headerName: "Situación",
        flex: 0.8,
        minWidth: 130,
      },
      {
        field: "vencimiento",
        headerName: "Vencimiento",
        flex: 0.8,
        minWidth: 140,
        valueGetter: (params) =>
          params.row.vencimiento ?? params.row.fecha_vencimiento ?? "",
      },
      {
        field: "capacidad",
        headerName: "Capacidad",
        flex: 0.7,
        minWidth: 130,
        valueGetter: (params) =>
          params.row.capacidad ??
          params.row.capacidad_carga ??
          params.row.capacidadCarga ??
          params.row.peso ??
          "",
      },
      {
        field: "photo",
        headerName: "Foto",
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const imageUrl = getPrimaryPhotoFromVehicle(params.row);
          const allUrls = getPhotoUrlsFromVehicle(params.row);

          if (imageUrl) {
            return (
              <Box
                component="img"
                src={imageUrl}
                alt={`Foto ${params.row.chapa || "vehículo"}`}
                onClick={(event) => {
                  if (allUrls.length) {
                    handlePhotoCellClick(event, params.row);
                  }
                }}
                sx={{
                  width: 100,
                  height: 60,
                  objectFit: "cover",
                  borderRadius: "4px",
                  border: `1px solid ${colors.grey[700]}`,
                  cursor: allUrls.length ? "pointer" : "default",
                }}
              />
            );
          }

          return (
            <Box component="span" sx={{ fontSize: "0.75rem", color: colors.grey[300] }}>
              Sin foto
            </Box>
          );
        },
      },
      ],
      [handleMenuClick, handlePhotoCellClick, colors]
  );

  return (
    <Box m="20px">
      <Header title="Vehículos" subtitle="Consulta y edición de vehículos" />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box display="flex" alignItems="center" gap="16px" mb="20px">
        <Box
          display="flex"
          alignItems="center"
          backgroundColor={colors.primary[400]}
          borderRadius="4px"
          px={2}
          sx={{ flex: 1, maxWidth: "420px" }}
        >
          <InputBase
            placeholder="Buscar por chapa, marca o propietario"
            value={searchValue}
            onChange={handleSearchChange}
            sx={{ flex: 1 }}
          />
          <IconButton>
            <SearchIcon />
          </IconButton>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => loadVehicles()}
          disabled={isFetching}
          sx={{ minWidth: 140 }}
        >
          {isFetching ? <CircularProgress size={18} /> : "Actualizar"}
        </Button>
      </Box>

      <Box
        sx={{
          height: "70vh",
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
        }}
      >
        <DataGrid
          rows={filteredVehicles}
          columns={columns}
          loading={isFetching}
          getRowId={(row) =>
            row.id ?? `${row.chapa || "vehiculo"}-${row.chasis || row.model || "na"}`
          }
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>Editar vehículo</MenuItem>
      </Menu>

      <VehicleEditDialog
        open={isEditDialogOpen}
        onClose={handleDialogClose}
        vehicle={selectedVehicle}
        onSubmit={handleEditSubmit}
        onPreviewPhotos={openPhotoPreview}
      />
      <PhotoPreviewDialog
        open={isPhotoPreviewOpen}
        photos={photoPreviewUrls}
        onClose={closePhotoPreview}
      />
    </Box>
  );
};

export default TruckQuery;

const DinatranAutoLookupWatcher = ({ values, lastLookupRef, onSchedule, onCancel, onTrigger }) => {
  const timerRef = useRef(null);

  useEffect(() => {
    const chapaInput = values.chapa ? values.chapa.trim() : "";
    const normalizedChapa = chapaInput.toUpperCase();
    const selectedCountry = values.country || "";

    if (normalizedChapa.length > 5 && selectedCountry) {
      const alreadyFetched =
        normalizedChapa === lastLookupRef.current.chapa &&
        selectedCountry === lastLookupRef.current.country;

      if (alreadyFetched) {
        onCancel();
        return;
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      onSchedule();
      timerRef.current = setTimeout(() => {
        onTrigger({ chapa: normalizedChapa, country: selectedCountry });
        timerRef.current = null;
      }, 1000);
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      onCancel();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [values.chapa, values.country, lastLookupRef, onSchedule, onCancel, onTrigger]);

  return null;
};
